import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Reports whether an element's text is visually clipped by `truncate`.
 *
 * Truncated labels are the case a tooltip exists for, and CSS cannot express
 * "only show the tooltip when the text actually overflows", so it has to be
 * measured. Re-measures on resize via ResizeObserver.
 */
export function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  return { ref, isTruncated };
}
